import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { bulkConvertOrdersToTestBilling } from '@utilities/order';
import type { BulkTestBillingProgressPhase } from '@utilities/order';
import { logError } from '@src/app/actions/userActions';
import type {
    BulkTestBillingItem,
    BulkTestBillingPhase,
    BulkTestBillingSummary,
} from '../components/BulkTestBillingModal';

type ReportRow = {
    orderId?: string;
    orderSeqNo?: string;
    uoId?: string;
    isGstBill?: boolean;
    selectedPayment?: string;
};

type BulkGstModalState = {
    open: boolean;
    phase: BulkTestBillingPhase;
    items: BulkTestBillingItem[];
    summary: BulkTestBillingSummary | null;
};

const INITIAL_MODAL_STATE: BulkGstModalState = {
    open: false,
    phase: 'confirm',
    items: [],
    summary: null,
};

const mapProgressPhaseToItemStatus = (
    phase: BulkTestBillingProgressPhase
): BulkTestBillingItem['status'] => {
    switch (phase) {
        case 'done':
            return 'done';
        case 'skip':
            return 'skipped';
        case 'error':
            return 'error';
        default:
            return 'processing';
    }
};

/** Collect unique GST food orders with pending payment eligible for bulk test-billing. */
export const collectEligibleBulkTestBillingItems = (
    rows: ReportRow[] | null | undefined
): BulkTestBillingItem[] => {
    const seen = new Set<string>();
    const items: BulkTestBillingItem[] = [];

    for (const row of rows || []) {
        if (
            !row?.orderId ||
            row.orderId === 'Total:' ||
            row.isGstBill !== true ||
            (row.selectedPayment || '').toLowerCase() !== 'pending' ||
            seen.has(row.orderId)
        ) {
            continue;
        }
        seen.add(row.orderId);
        items.push({
            orderId: row.orderId,
            label: row.orderSeqNo || row.uoId || row.orderId,
            status: 'pending',
        });
    }

    return items;
};

export type UseBulkTestBillingOptions = {
    /** 0 = food orders, non-zero = room bills */
    serviceType: number;
    localReportData: ReportRow[];
    setLocalReportData: Dispatch<SetStateAction<any[]>>;
    dispatch: (action: ReturnType<typeof logError>) => void;
    onRefresh: () => void;
};

export const useBulkTestBilling = ({
    serviceType,
    localReportData,
    setLocalReportData,
    dispatch,
    onRefresh,
}: UseBulkTestBillingOptions) => {
    const inFlightRef = useRef(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [modalState, setModalState] = useState<BulkGstModalState>(INITIAL_MODAL_STATE);

    const updateItem = (orderId: string, patch: Partial<BulkTestBillingItem>) => {
        setModalState((prev) => ({
            ...prev,
            items: prev.items.map((item) =>
                item.orderId === orderId ? { ...item, ...patch } : item
            ),
        }));
    };

    const openBulkTestBilling = () => {
        if (serviceType !== 0 || isUpdating || inFlightRef.current) {
            return;
        }
        const items = collectEligibleBulkTestBillingItems(localReportData);
        if (!items.length) {
            return;
        }
        setModalState({ open: true, phase: 'confirm', items, summary: null });
    };

    const handleConfirm = async () => {
        const orderIds = modalState.items.map((item) => item.orderId);
        if (!orderIds.length || serviceType !== 0 || inFlightRef.current) {
            return;
        }

        inFlightRef.current = true;
        setIsUpdating(true);
        setModalState((prev) => ({
            ...prev,
            phase: 'running',
            summary: null,
            items: prev.items.map((item) => ({
                ...item,
                status: 'pending',
                message: undefined,
                copyDocId: undefined,
            })),
        }));

        try {
            const { updatesByOrderId, summary } = await bulkConvertOrdersToTestBilling(orderIds, {
                onProgress: ({ orderId, phase, message, copyDocId }) => {
                    updateItem(orderId, {
                        status: mapProgressPhaseToItemStatus(phase),
                        message,
                        copyDocId,
                    });
                },
            });

            setLocalReportData((prevData: any[]) =>
                prevData.map((item: any) =>
                    updatesByOrderId[item.orderId]
                        ? { ...item, ...updatesByOrderId[item.orderId] }
                        : item
                )
            );

            setModalState((prev) => ({
                ...prev,
                phase: 'complete',
                summary,
                items: prev.items.map((item) => {
                    const errorEntry = summary.errors.find((e) => e.orderId === item.orderId);
                    if (errorEntry) {
                        return { ...item, status: 'error' as const, message: errorEntry.message };
                    }
                    if (updatesByOrderId[item.orderId] && item.status === 'pending') {
                        return { ...item, status: 'done' as const, message: item.message || 'Converted' };
                    }
                    return item;
                }),
            }));

            onRefresh();

            if (summary.failed > 0) {
                dispatch(logError({
                    severity: 'error',
                    message: `Bulk test-billing: ${summary.failed} order(s) failed. ${summary.succeeded} succeeded, ${summary.skipped} skipped.`,
                }));
            } else if (summary.succeeded > 0) {
                dispatch(logError({
                    severity: 'success',
                    message: `Test-billing migration complete: ${summary.succeeded} converted${summary.skipped ? `, ${summary.skipped} skipped` : ''}.`,
                }));
            }
        } catch (error) {
            console.error('Error bulk updating GST status:', error);
            dispatch(logError({
                severity: 'error',
                message: (error as Error)?.message || 'Failed to update GST status',
            }));
            setModalState((prev) => ({ ...prev, phase: 'complete' }));
        } finally {
            inFlightRef.current = false;
            setIsUpdating(false);
        }
    };

    const handleClose = () => {
        if (inFlightRef.current) {
            return;
        }
        setModalState(INITIAL_MODAL_STATE);
    };

    return {
        isUpdating,
        openBulkTestBilling,
        bulkTestBillingModalProps: {
            open: modalState.open,
            phase: modalState.phase,
            items: modalState.items,
            summary: modalState.summary,
            onConfirm: handleConfirm,
            onClose: handleClose,
        },
    };
};
